import { useState, useEffect, useMemo } from 'react';

// ── Types ────────────────────────────────────────────────────────────────────

type Phase = 'memorize' | 'recall' | 'result' | 'complete';

interface Round {
  round_id: number;
  grid_size: string;
  grid: string[];
  hidden_positions: number[]; // 1-indexed
  bank_emojis?: string[];     // correct answers + decoys; always use this over full grid
}

interface RoundResult {
  round_id: number;
  correct: boolean[];
  round_score: number;
  total_possible: number;
  correct_answers: string[];
  user_answers: string[];
}

interface Props {
  nanoid: string;
  memoryGrid: { rounds: Round[] };
}

const MEMORIZE_SECONDS = 10;

// ── Component ─────────────────────────────────────────────────────────────────

export default function MemoryGridPuzzle({ nanoid, memoryGrid }: Props) {
  const rounds = useMemo(() => memoryGrid?.rounds ?? [], [memoryGrid]);

  const [roundIdx, setRoundIdx] = useState(0);
  const [phase, setPhase] = useState<Phase>('memorize');
  const [countdown, setCountdown] = useState(MEMORIZE_SECONDS);
  // One answer slot per hidden position in current round
  const [userAnswers, setUserAnswers] = useState<(string | null)[]>([]);
  // Which blank (by blank index) is currently focused for emoji selection
  const [focusedBlank, setFocusedBlank] = useState<number | null>(null);
  const [roundResults, setRoundResults] = useState<RoundResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const round = rounds[roundIdx] ?? null;
  const hiddenCount = round?.hidden_positions?.length ?? 0;

  // Reset state when round index changes
  useEffect(() => {
    setPhase('memorize');
    setCountdown(MEMORIZE_SECONDS);
    setUserAnswers(Array(hiddenCount).fill(null));
    setFocusedBlank(null);
    setError(null);
  }, [roundIdx, hiddenCount]);

  // Countdown during memorize phase
  useEffect(() => {
    if (phase !== 'memorize') return;
    if (countdown === 0) { setPhase('recall'); setFocusedBlank(0); return; }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, countdown]);

  // Shuffled emoji bank — uses bank_emojis (answers + decoys) so players can't solve by elimination
  const bankEmojis = useMemo(() => {
    if (!round) return [];
    const source = round.bank_emojis?.length ? [...round.bank_emojis] : [...round.grid];
    for (let i = source.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [source[i], source[j]] = [source[j], source[i]];
    }
    return source;
  }, [round?.round_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Interaction ─────────────────────────────────────────────────────────────

  function pickEmoji(emoji: string) {
    if (phase !== 'recall' || focusedBlank === null) return;
    const next = [...userAnswers];
    next[focusedBlank] = emoji;
    setUserAnswers(next);
    // Auto-advance focus to next unfilled blank
    const nextEmpty = next.findIndex((a, i) => i > focusedBlank && a === null);
    const anyEmpty = next.findIndex(a => a === null);
    setFocusedBlank(nextEmpty !== -1 ? nextEmpty : anyEmpty !== -1 ? anyEmpty : null);
  }

  async function submitRound() {
    if (!round || loading) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/puzzles/${nanoid}/memory/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ round_id: round.round_id, answers: userAnswers }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed');
      setRoundResults(prev => [...prev, {
        round_id: round.round_id,
        ...data.data,
        user_answers: [...(userAnswers as string[])],
      }]);
      setPhase('result');
    } catch {
      setError('Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  }

  function advanceRound() {
    if (roundIdx + 1 >= rounds.length) {
      setPhase('complete');
    } else {
      setRoundIdx(r => r + 1);
    }
  }

  // ── Render helpers ──────────────────────────────────────────────────────────

  if (!round && phase !== 'complete') return null;

  const allAnswered = userAnswers.length > 0 && userAnswers.every(a => a !== null);
  const totalScore = roundResults.reduce((s, r) => s + r.round_score, 0);
  const totalPossible = roundResults.reduce((s, r) => s + r.total_possible, 0);

  // ── Complete screen ─────────────────────────────────────────────────────────

  if (phase === 'complete') {
    return (
      <div className="pz-memory">
        <div className="pz-verdict-banner pz-verdict-correct">
          All done!
        </div>
        <div className="pz-mg-final-score">
          <span className="pz-mg-final-num">{totalScore}</span>
          <span className="pz-mg-final-denom">/ {totalPossible}</span>
        </div>
        <div className="pz-mg-round-summary">
          {roundResults.map(r => (
            <div key={r.round_id} className="pz-mg-round-row">
              <span>Round {r.round_id}</span>
              <span>{r.round_score}/{r.total_possible}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Grid rendering ──────────────────────────────────────────────────────────

  const hiddenSet = new Set(round.hidden_positions); // 1-indexed

  function renderGrid() {
    return (
      <div className="pz-mg-grid">
        {round.grid.map((emoji, i) => {
          const pos = i + 1; // 1-indexed
          const isHidden = hiddenSet.has(pos);
          const blankIdx = round.hidden_positions.indexOf(pos);

          if (!isHidden || phase === 'memorize') {
            // Show emoji normally
            return (
              <div key={i} className="pz-mg-cell">
                {emoji}
              </div>
            );
          }

          // Hidden position during recall / result
          const filled = userAnswers[blankIdx] ?? null;
          const isFocused = phase === 'recall' && focusedBlank === blankIdx;

          if (phase === 'result') {
            const result = roundResults[roundResults.length - 1];
            const wasCorrect = result?.correct[blankIdx];
            return (
              <div
                key={i}
                className={`pz-mg-cell pz-mg-blank ${wasCorrect ? 'pz-mg-correct' : 'pz-mg-wrong'}`}
              >
                {wasCorrect ? filled : result?.correct_answers[blankIdx]}
              </div>
            );
          }

          return (
            <button
              key={i}
              className={`pz-mg-cell pz-mg-blank ${filled ? 'pz-mg-filled' : ''} ${isFocused ? 'pz-mg-focused' : ''}`}
              onClick={() => setFocusedBlank(blankIdx)}
            >
              {filled ?? '?'}
            </button>
          );
        })}
      </div>
    );
  }

  // ── Main render ─────────────────────────────────────────────────────────────

  return (
    <div className="pz-memory">
      {/* Round indicator */}
      <div className="pz-mg-round-indicator">
        {rounds.map((_, i) => (
          <div
            key={i}
            className={`pz-mg-round-dot ${i < roundIdx ? 'pz-mg-dot-done' : i === roundIdx ? 'pz-mg-dot-active' : ''}`}
          />
        ))}
        <span className="pz-mg-round-label">Round {round.round_id} of {rounds.length}</span>
      </div>

      {/* Memorize phase */}
      {phase === 'memorize' && (
        <>
          <div className="pz-mg-phase-label">
            Memorise the grid!
            <span className="pz-mg-countdown">{countdown}</span>
          </div>
          {renderGrid()}
        </>
      )}

      {/* Recall phase */}
      {phase === 'recall' && (
        <>
          <p className="pz-mg-phase-label pz-mg-phase-recall">
            {hiddenCount === 1 ? 'Which emoji was hidden?' : `Which ${hiddenCount} emojis were hidden?`}
          </p>
          {renderGrid()}

          {/* Emoji bank */}
          <div className="pz-mg-bank">
            {bankEmojis.map((emoji, i) => (
              <button
                key={i}
                className={`pz-mg-bank-btn ${userAnswers.includes(emoji) ? 'pz-mg-bank-used' : ''}`}
                onClick={() => pickEmoji(emoji)}
                disabled={focusedBlank === null}
              >
                {emoji}
              </button>
            ))}
          </div>

          {error && <p className="pz-error">{error}</p>}

          <button
            className="pz-btn-primary"
            onClick={submitRound}
            disabled={!allAnswered || loading}
          >
            {loading ? 'Checking...' : 'Submit'}
          </button>
        </>
      )}

      {/* Result phase */}
      {phase === 'result' && (() => {
        const result = roundResults[roundResults.length - 1];
        return (
          <>
            <div className={`pz-verdict-banner ${result.round_score === result.total_possible ? 'pz-verdict-correct' : 'pz-verdict-wrong'}`}>
              {result.round_score}/{result.total_possible} correct
            </div>
            {renderGrid()}
            <div className="pz-mg-result-detail">
              {round.hidden_positions.map((pos, i) => (
                <div key={i} className={`pz-mg-result-row ${result.correct[i] ? 'pz-mg-res-ok' : 'pz-mg-res-miss'}`}>
                  <span>Position {pos}</span>
                  <span>{result.correct[i] ? '✓' : `✗  answer: ${result.correct_answers[i]}`}</span>
                </div>
              ))}
            </div>
            <button className="pz-btn-primary" onClick={advanceRound}>
              {roundIdx + 1 >= rounds.length ? 'See Final Score' : 'Next Round'}
            </button>
          </>
        );
      })()}
    </div>
  );
}
